import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Instagram, MessageCircle, Mail, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SITE, CONSULTANCY_SERVICES, CLEANING_SERVICES } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  ...CONSULTANCY_SERVICES.map((s) => `Consultancy · ${s.title}`),
  ...CLEANING_SERVICES.map((s) => `Cleaning · ${s.title}`),
  "Something else",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please fill name, email, service and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we'll be in touch within 24 hours.");
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't send your message. Please try WhatsApp instead."
      );
    } finally {
      setLoading(false);
    }
  };

  const waLink = `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(
    "Hi Nexaverve — I'd like to enquire about your services."
  )}`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: copy + channels */}
          <div className="lg:col-span-5">
            <p className="tag-label mb-5">// Section 04 / Contact</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-white leading-[1.05]">
              Let&apos;s scope your <span className="text-[#FBBF24]">project.</span>
            </h2>
            <p className="mt-6 text-neutral-400 text-base sm:text-lg leading-relaxed max-w-md">
              Tell us what you need. We&apos;ll get back within 24 hours with a clear plan and a fixed quote.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="group flex items-center justify-between border border-white/10 hover:border-[#FBBF24]/60 p-5 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <MessageCircle className="w-5 h-5 text-[#FBBF24]" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                      WhatsApp
                    </div>
                    <div className="text-white font-semibold mt-0.5">
                      {SITE.whatsappPretty}
                    </div>
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-neutral-500 group-hover:text-[#FBBF24] transition-colors" />
              </a>

              <a
                href={SITE.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="group flex items-center justify-between border border-white/10 hover:border-[#FBBF24]/60 p-5 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Instagram className="w-5 h-5 text-[#FBBF24]" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                      Instagram
                    </div>
                    <div className="text-white font-semibold mt-0.5">
                      @{SITE.instagramHandle}
                    </div>
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-neutral-500 group-hover:text-[#FBBF24] transition-colors" />
              </a>

              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email"
                className="group flex items-center justify-between border border-white/10 hover:border-[#FBBF24]/60 p-5 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-[#FBBF24]" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                      Email
                    </div>
                    <div className="text-white font-semibold mt-0.5">
                      {SITE.email}
                    </div>
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-neutral-500 group-hover:text-[#FBBF24] transition-colors" />
              </a>

              <div className="flex items-center gap-4 p-5 border border-white/10">
                <MapPin className="w-5 h-5 text-[#FBBF24]" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                    Location
                  </div>
                  <div className="text-white font-semibold mt-0.5">
                    {SITE.location}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="border border-white/10 bg-[#0A0A0A] p-6 sm:p-10"
            >
              {submitted ? (
                <div
                  data-testid="contact-form-success"
                  className="flex flex-col items-start gap-4 py-10"
                >
                  <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#FBBF24]">
                    [ Submitted ]
                  </span>
                  <h3 className="font-display text-3xl font-bold text-white">
                    Thanks — message received.
                  </h3>
                  <p className="text-neutral-400 max-w-md">
                    We&apos;ll review your enquiry and reply within 24 hours.
                    Need it sooner? Ping us on WhatsApp.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-form-reset"
                    className="nx-btn-secondary mt-2"
                    onClick={() => setSubmitted(false)}
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-xs uppercase tracking-[0.2em] text-neutral-400">
                        Name *
                      </Label>
                      <Input
                        id="name"
                        data-testid="contact-input-name"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Full name"
                        className="mt-2 bg-neutral-950 border-neutral-800 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#FBBF24] focus-visible:border-[#FBBF24] h-12"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-neutral-400">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-input-email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@company.co.uk"
                        className="mt-2 bg-neutral-950 border-neutral-800 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#FBBF24] focus-visible:border-[#FBBF24] h-12"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] text-neutral-400">
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="Optional"
                        className="mt-2 bg-neutral-950 border-neutral-800 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#FBBF24] focus-visible:border-[#FBBF24] h-12"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">
                        Service *
                      </Label>
                      <Select value={form.service} onValueChange={update("service")}>
                        <SelectTrigger
                          data-testid="contact-input-service"
                          className="mt-2 bg-neutral-950 border-neutral-800 text-white rounded-none focus:ring-1 focus:ring-[#FBBF24] focus:border-[#FBBF24] h-12"
                        >
                          <SelectValue placeholder="Pick a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-neutral-950 border-neutral-800 text-white rounded-none">
                          {SERVICE_OPTIONS.map((opt) => (
                            <SelectItem
                              key={opt}
                              value={opt}
                              data-testid={`service-option-${opt.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                              className="rounded-none focus:bg-[#FBBF24] focus:text-black"
                            >
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-5">
                    <Label htmlFor="message" className="text-xs uppercase tracking-[0.2em] text-neutral-400">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us about the property, size, urgency, anything that helps us price accurately."
                      rows={5}
                      className="mt-2 bg-neutral-950 border-neutral-800 text-white rounded-none focus-visible:ring-1 focus-visible:ring-[#FBBF24] focus-visible:border-[#FBBF24]"
                      required
                    />
                  </div>

                  <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-600">
                      We reply within 24h · Mon–Sat
                    </p>
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={loading}
                      className="nx-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          Send Enquiry <ArrowUpRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
